// ABOUTME: Stock management component for viewing and adjusting inventory levels.
// ABOUTME: Displays active, inactive, and ignored products with sortable tables.

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  ErrorOutline as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Block as BlockIcon,
  PauseCircleOutline as InactiveIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import MemberBreakdown from './schedule/MemberBreakdown';
import { useTranslation } from 'react-i18next';
import { format, isSameDay, parseISO } from 'date-fns';
import axios from 'axios';
import API_BASE_URL from '../config';
import { colors } from '../theme/colors';
import SortableHeaderCell from './schedule/SortableHeaderCell';
import useSortableTable from '../hooks/useSortableTable';
import { formatHorizon, formatTime } from '../utils/formatters';

function StockAdjustment({ shopId, testModeEnabled, currentTime, onStockChange, onProductSelect }) {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [recentAdjustments, setRecentAdjustments] = useState([]);
  const [stockLevels, setStockLevels] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);
  const [ignoredExpanded, setIgnoredExpanded] = useState(false);
  const [expandedMemberRows, setExpandedMemberRows] = useState({});
  const [stockTimestamp, setStockTimestamp] = useState(
    new Date().toISOString().slice(0, 16) // Format for datetime-local input
  );

  // Store products in a ref so fetchStockLevels always has access to latest
  const productsRef = React.useRef([]);
  const categoriesRef = React.useRef({});

  useEffect(() => {
    if (!shopId) return;

    // Fetch products and categories first, then stock levels
    const initData = async () => {
      await fetchCategories();
      await fetchProducts();
      await fetchStockLevels();
    };
    initData();

    // Auto-refresh stock levels every 30 seconds (not products)
    const interval = setInterval(() => {
      fetchStockLevels();
    }, 30000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const fetchCategories = async () => {
    if (!shopId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/shops/${shopId}/baker/categories`);
      const categoriesList = Array.isArray(response.data) ? response.data : [];
      // Index by category id for easy lookup
      const categoriesMap = {};
      categoriesList.forEach(cat => {
        categoriesMap[cat.id] = cat;
      });
      categoriesRef.current = categoriesMap;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    if (!shopId) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/shops/${shopId}/baker/products`);
      // response.data is now an array directly
      const productsList = Array.isArray(response.data) ? response.data : [];
      // Enrich products with category object
      const enrichedProducts = productsList.map(p => ({
        ...p,
        category: p.category ? categoriesRef.current[p.category] : null,
      }));
      setProducts(enrichedProducts);
      productsRef.current = enrichedProducts;
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchStockLevels = async () => {
    if (!shopId) return;
    try {
      setStockLoading(true);
      const response = await axios.get(`${API_BASE_URL}/shops/${shopId}/baker/stock-levels`);
      if (response.data.stock_levels) {
        // Merge stock data with product metadata from ref
        const stockMap = new Map(response.data.stock_levels.map(s => [s.product_id, s]));
        const currentProducts = productsRef.current;

        if (currentProducts.length > 0) {
          // Merge with products
          setStockLevels(currentProducts.map(p => ({
            ...p,
            ...stockMap.get(p.product_id),
          })));
        } else {
          // Fallback: use stock levels as-is (no product names)
          setStockLevels(response.data.stock_levels);
        }
      }
    } catch (error) {
      console.error('Failed to fetch stock levels:', error);
    } finally {
      setStockLoading(false);
    }
  };

  const handleAddStock = async () => {
    if (!selectedProduct || !quantity) {
      setMessage(t('stock.pleaseSelectProduct'));
      setMessageType('warning');
      return;
    }

    setLoading(true);
    try {
      let response;

      if (testModeEnabled) {
        // In test mode, use test-mode endpoint
        response = await axios.post(`${API_BASE_URL}/test-mode/adjust-stock`, {
          product: selectedProduct,
          quantity: parseFloat(quantity),
          reason: 'manual_adjustment',
        });
      } else {
        // In production mode, use baker update-inventory with timestamp
        let timestampToSend;

        if (showCustomTime) {
          // Convert datetime-local (which is in local time) to ISO string
          // The datetime-local input value is already in Budapest time, so we need to preserve it
          const localDateTime = new Date(stockTimestamp);
          // Format as ISO but specify it's in Budapest timezone by adding the offset
          const year = localDateTime.getFullYear();
          const month = String(localDateTime.getMonth() + 1).padStart(2, '0');
          const day = String(localDateTime.getDate()).padStart(2, '0');
          const hours = String(localDateTime.getHours()).padStart(2, '0');
          const minutes = String(localDateTime.getMinutes()).padStart(2, '0');
          const seconds = String(localDateTime.getSeconds()).padStart(2, '0');
          // Budapest is UTC+1 (standard) or UTC+2 (DST), use +02:00 for now (DST)
          // Note: This is a simplification - ideally use a library like date-fns-tz for proper DST handling
          timestampToSend = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+02:00`;
        } else {
          // Use current time (no timestamp sent, backend will use current time)
          timestampToSend = undefined;
        }

        response = await axios.post(`${API_BASE_URL}/shops/${shopId}/baker/update-inventory`, {
          product: selectedProduct,
          quantity: parseFloat(quantity),
          ...(timestampToSend && { timestamp: timestampToSend }),
        });
      }

      if (response.data.status === 'success' || response.data.status === 'updated') {
        setMessage(t('stock.addedSuccess', { quantity, product: selectedProduct }));
        setMessageType('success');

        // Add to recent adjustments
        setRecentAdjustments(prev => [
          {
            product: selectedProduct,
            quantity: parseFloat(quantity),
            timestamp: new Date().toISOString(),
          },
          ...prev.slice(0, 4), // Keep last 5
        ]);

        // Clear form
        setQuantity('');

        // Refresh stock levels
        fetchStockLevels();

        // Notify parent component that stock changed
        if (onStockChange) {
          onStockChange();
        }
      }
    } catch (error) {
      setMessage(t('stock.failedToAdjust'));
      setMessageType('error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStock = async () => {
    if (!selectedProduct || !quantity) {
      setMessage(t('stock.pleaseSelectProduct'));
      setMessageType('warning');
      return;
    }

    setLoading(true);
    try {
      let response;

      if (testModeEnabled) {
        // In test mode, use test-mode endpoint with negative quantity
        response = await axios.post(`${API_BASE_URL}/test-mode/adjust-stock`, {
          product: selectedProduct,
          quantity: -parseFloat(quantity),
          reason: 'manual_adjustment',
        });
      } else {
        // In production mode, use baker update-inventory with negative quantity and timestamp
        let timestampToSend;

        if (showCustomTime) {
          // Convert datetime-local (which is in local time) to ISO string
          // The datetime-local input value is already in Budapest time, so we need to preserve it
          const localDateTime = new Date(stockTimestamp);
          // Format as ISO but specify it's in Budapest timezone by adding the offset
          const year = localDateTime.getFullYear();
          const month = String(localDateTime.getMonth() + 1).padStart(2, '0');
          const day = String(localDateTime.getDate()).padStart(2, '0');
          const hours = String(localDateTime.getHours()).padStart(2, '0');
          const minutes = String(localDateTime.getMinutes()).padStart(2, '0');
          const seconds = String(localDateTime.getSeconds()).padStart(2, '0');
          // Budapest is UTC+1 (standard) or UTC+2 (DST), use +02:00 for now (DST)
          // Note: This is a simplification - ideally use a library like date-fns-tz for proper DST handling
          timestampToSend = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+02:00`;
        } else {
          // Use current time (no timestamp sent, backend will use current time)
          timestampToSend = undefined;
        }

        response = await axios.post(`${API_BASE_URL}/shops/${shopId}/baker/update-inventory`, {
          product: selectedProduct,
          quantity: -parseFloat(quantity),
          ...(timestampToSend && { timestamp: timestampToSend }),
        });
      }

      if (response.data.status === 'success' || response.data.status === 'updated') {
        setMessage(t('stock.removedSuccess', { quantity, product: selectedProduct }));
        setMessageType('success');

        // Add to recent adjustments
        setRecentAdjustments(prev => [
          {
            product: selectedProduct,
            quantity: -parseFloat(quantity),
            timestamp: new Date().toISOString(),
          },
          ...prev.slice(0, 4),
        ]);

        // Clear form
        setQuantity('');

        // Refresh stock levels
        fetchStockLevels();

        // Notify parent component that stock changed
        if (onStockChange) {
          onStockChange();
        }
      }
    } catch (error) {
      setMessage(t('stock.failedToAdjust'));
      setMessageType('error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchProduced = async (product) => {
    if (!testModeEnabled) {
      setMessage(t('stock.batchOnlyInTestMode'));
      setMessageType('info');
      return;
    }

    setLoading(true);
    try {
      // Get batch size from product config (default 20)
      const response = await axios.post(`${API_BASE_URL}/test-mode/batch-produced`, {
        product: product || selectedProduct,
        quantity: 20, // Default batch size, should get from config
      });

      if (response.data.status === 'success') {
        setMessage(t('stock.batchProducedSuccess', { product: product || selectedProduct }));
        setMessageType('success');
      }
    } catch (error) {
      setMessage(t('stock.failedBatchProduction'));
      setMessageType('error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format transaction time
  const formatTransactionTime = (dateTimeString, referenceDate) => {
    if (!dateTimeString) return '-';
    try {
      const date = parseISO(dateTimeString);
      const isReferenceDay = isSameDay(date, referenceDate);
      if (isReferenceDay) {
        return format(date, 'HH:mm:ss');
      } else {
        return format(date, 'yyyy-MM-dd HH:mm:ss');
      }
    } catch (e) {
      return 'Invalid';
    }
  };

  // Shared row renderer for both active and ignored products
  const renderStockRow = (item, isIgnored = false) => {
    const referenceDate = currentTime || new Date();
    const transactionTime = formatTransactionTime(item.last_transaction, referenceDate);
    const hasTodayData = item.last_transaction && isSameDay(parseISO(item.last_transaction), referenceDate);
    const isNotToday = item.last_transaction && !hasTodayData;
    const categoryColor = item.category?.color || colors.category.default;
    const hasMembers = item.members?.length > 0;
    // Virtual shop breakdowns are open by default; a click collapses them
    const membersExpanded = hasMembers && (expandedMemberRows[item.product_id] ?? true);

    const handleRowClick = () => {
      // Virtual shop rows expand to the member breakdown instead of selecting for the chart
      if (hasMembers) {
        setExpandedMemberRows((expanded) => ({ ...expanded, [item.product_id]: !(expanded[item.product_id] ?? true) }));
        return;
      }
      setSelectedProduct(item.product_id);
      if (onProductSelect) {
        onProductSelect(item.product_id);
      }
    };

    return (
      <React.Fragment key={item.product_id}>
      <Box
        onClick={handleRowClick}
        sx={{
          display: 'grid',
          gridTemplateColumns: '3fr 1.2fr 0.7fr 0.7fr 0.7fr 0.8fr 0.7fr 0.7fr',
          alignItems: 'center',
          py: 1.5,
          px: 2,
          cursor: 'pointer',
          bgcolor: selectedProduct === item.product_id ? colors.background.light : colors.background.white,
          opacity: isIgnored ? 0.6 : 1,
          borderBottom: `1px solid ${colors.border.lighter}`,
          '&:hover': {
            bgcolor: colors.background.hover,
          },
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        {/* Product Name - with color indicator, product ID, and name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minHeight: '40px',
          }}
        >
          {/* Category color indicator */}
          <Box
            sx={{
              width: '6px',
              height: '32px',
              borderRadius: '3px',
              bgcolor: categoryColor,
              flexShrink: 0,
              ml: -0.5,
            }}
          />
          {hasMembers && (
            <ExpandMoreIcon
              sx={{
                fontSize: '1.1rem',
                color: colors.text.secondary,
                flexShrink: 0,
                transform: membersExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          )}
          {!hasTodayData && (
            <WarningIcon fontSize="small" sx={{ color: colors.status.error, flexShrink: 0 }} />
          )}
          {item.product_id && (
            <Box
              sx={{
                fontSize: '0.75rem',
                color: colors.text.muted,
                flexShrink: 0,
              }}
            >
              #{item.product_id}
            </Box>
          )}
          {item.config_warning === 'missing_params' ? (
            <Box sx={{ fontSize: '0.8rem', color: colors.status.warning, fontStyle: 'italic' }}>
              {t('warnings.missingParams')}
            </Box>
          ) : (
            <Box
              sx={{
                fontWeight: 600,
                color: colors.text.primary,
                fontSize: '0.875rem',
                lineHeight: 1.3,
              }}
            >
              {item.name || `#${item.product_id}`}
            </Box>
          )}
          {item.name_mismatch && (
            <Tooltip title={t('warnings.nameMismatch')}>
              <WarningAmberIcon sx={{ fontSize: '1rem', color: colors.status.warning, flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>

        {/* Category */}
        <Box sx={{ textAlign: 'center', fontSize: '0.875rem', color: colors.text.secondary }}>
          {item.config_warning === 'missing_category' ? (
            <Box component="span" sx={{ color: colors.status.warning, fontStyle: 'italic' }}>
              {t('warnings.missingCategory')}
            </Box>
          ) : (
            item.category?.name || '-'
          )}
        </Box>

        {/* Current Stock */}
        <Box
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            fontWeight: (item.stock ?? 0) !== 0 ? 600 : 400,
            color: (item.stock ?? 0) < 0 ? colors.status.error : (item.stock ?? 0) === 0 ? colors.text.secondary : colors.text.primary,
          }}
        >
          {(item.stock ?? 0).toFixed(0)}
        </Box>

        {/* Prediction Horizon */}
        <Box sx={{ textAlign: 'center', fontSize: '0.875rem', color: colors.text.secondary }}>
          {formatHorizon(item.prediction_horizon_minutes)}
        </Box>

        {/* Expected Demand */}
        <Box sx={{ textAlign: 'center', fontSize: '0.875rem', color: colors.text.primary }}>
          {item.expected_demand != null ? Math.round(item.expected_demand) : '-'}
        </Box>

        {/* Depletes At */}
        <Box
          sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: (item.stock ?? 0) <= 0 ? colors.status.error : colors.text.primary,
            fontWeight: item.stock_depletes_at || item.remaining_at_close != null ? 600 : 400,
          }}
        >
          {(item.stock ?? 0) <= 0
            ? t('dashboard.schedule.urgent.depleted')
            : item.stock_depletes_at
              ? formatTime(item.stock_depletes_at)
              : item.remaining_at_close != null && item.effective_closing_time
                ? t('stock.remainsAtClose', { time: item.effective_closing_time, units: item.remaining_at_close })
                : '-'}
        </Box>

        {/* Last Transaction */}
        <Box
          sx={{
            textAlign: 'center',
            fontSize: '0.75rem',
            fontWeight: isNotToday ? 600 : 400,
            color: isNotToday ? colors.status.warning : colors.text.secondary,
          }}
        >
          {transactionTime}
        </Box>

        {/* Status */}
        <Box sx={{ textAlign: 'center' }}>
          {(item.stock ?? 0) > 0 ? (
            <Chip
              label={t('stock.inStock')}
              size="small"
              sx={{ bgcolor: colors.schedule.planned, color: 'white' }}
            />
          ) : (item.stock ?? 0) < 0 ? (
            <Chip
              label={t('stock.negative')}
              size="small"
              sx={{ bgcolor: colors.status.error, color: 'white' }}
            />
          ) : (
            <Chip
              label={t('stock.outOfStock')}
              size="small"
              sx={{ bgcolor: colors.text.muted, color: 'white' }}
            />
          )}
        </Box>
      </Box>
      {membersExpanded && (
        <MemberBreakdown
          members={item.members}
          parentName={item.name}
          variant="stock"
          formatTransaction={(iso) => formatTransactionTime(iso, referenceDate)}
        />
      )}
      </React.Fragment>
    );
  };

  // Active products: is_active === true (not ignored, has enough recent transactions)
  const activeProductsRaw = stockLevels.filter(item => !item.ignore && item.is_active === true);
  // Inactive products: is_active === false (not ignored, but not enough recent transactions)
  const inactiveProductsRaw = stockLevels.filter(item => !item.ignore && item.is_active === false);
  // Ignored products: ignore === true
  const ignoredProductsRaw = stockLevels.filter(item => item.ignore);

  // Sortable tables for each section
  const {
    sortedItems: activeProducts,
    sortKey: activeSortKey,
    sortDirection: activeSortDirection,
    handleSort: handleActiveSort,
    isSortable: isActiveSortable,
  } = useSortableTable(activeProductsRaw, {
    defaultSortKey: 'stock',
    defaultSortDirection: 'desc',
  });

  const {
    sortedItems: inactiveProducts,
    sortKey: inactiveSortKey,
    sortDirection: inactiveSortDirection,
    handleSort: handleInactiveSort,
    isSortable: isInactiveSortable,
  } = useSortableTable(inactiveProductsRaw, {
    defaultSortKey: 'stock',
    defaultSortDirection: 'desc',
  });

  const {
    sortedItems: ignoredProducts,
    sortKey: ignoredSortKey,
    sortDirection: ignoredSortDirection,
    handleSort: handleIgnoredSort,
    isSortable: isIgnoredSortable,
  } = useSortableTable(ignoredProductsRaw, {
    defaultSortKey: 'stock',
    defaultSortDirection: 'desc',
  });

  const TableHeader = ({ currentSortKey, sortDirection, onSort, isSortable }) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '3fr 1.2fr 0.7fr 0.7fr 0.7fr 0.8fr 0.7fr 0.7fr',
        py: 1.5,
        px: 2,
        bgcolor: colors.schedule.stock,
        color: 'white',
        fontSize: '0.8125rem',
        fontWeight: 600,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SortableHeaderCell
          sortKey="product_id"
          currentSortKey={currentSortKey}
          sortDirection={sortDirection}
          onSort={onSort}
          isSortable={isSortable('product_id')}
        >
          {t('dashboard.schedule.table.id')}
        </SortableHeaderCell>
        <Box>/</Box>
        <SortableHeaderCell
          sortKey="name"
          currentSortKey={currentSortKey}
          sortDirection={sortDirection}
          onSort={onSort}
          isSortable={isSortable('name')}
        >
          {t('dashboard.schedule.table.product')}
        </SortableHeaderCell>
      </Box>
      <SortableHeaderCell
        sortKey="category.name"
        currentSortKey={currentSortKey}
        sortDirection={sortDirection}
        onSort={onSort}
        isSortable={isSortable('category.name')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.table.category')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="stock"
        currentSortKey={currentSortKey}
        sortDirection={sortDirection}
        onSort={onSort}
        isSortable={isSortable('stock')}
        sx={{ textAlign: 'center' }}
      >
        {t('stock.currentStock')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="prediction_horizon_minutes"
        currentSortKey={currentSortKey}
        sortDirection={sortDirection}
        onSort={onSort}
        isSortable={isSortable('prediction_horizon_minutes')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.table.horizon')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="expected_demand"
        currentSortKey={currentSortKey}
        sortDirection={sortDirection}
        onSort={onSort}
        isSortable={isSortable('expected_demand')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.recommendations.demand')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="stock_depletes_at"
        currentSortKey={currentSortKey}
        sortDirection={sortDirection}
        onSort={onSort}
        isSortable={isSortable('stock_depletes_at')}
        sx={{ textAlign: 'center' }}
      >
        {t('dashboard.schedule.urgent.depletesAt')}
      </SortableHeaderCell>
      <SortableHeaderCell
        sortKey="last_transaction"
        currentSortKey={currentSortKey}
        sortDirection={sortDirection}
        onSort={onSort}
        isSortable={isSortable('last_transaction')}
        sx={{ textAlign: 'center' }}
      >
        {t('stock.lastTransaction')}
      </SortableHeaderCell>
      <Box sx={{ textAlign: 'center' }}>{t('stock.status')}</Box>
    </Box>
  );

  return (
    <Box>
      {/* Section Title */}
      <Box
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: colors.schedule.stockDark,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {t('stock.currentStockLevels')}
        {testModeEnabled && (
          <Box component="span" sx={{ fontSize: '0.75rem', color: colors.status.warning }}>
            ({t('dashboard.testMode')})
          </Box>
        )}
        <IconButton onClick={fetchStockLevels} disabled={stockLoading} size="small" sx={{ ml: 'auto' }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {stockLoading && stockLevels.length === 0 ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Active Products Table */}
          <Box
            sx={{
              bgcolor: colors.background.white,
              borderRadius: '12px',
              border: `3px solid ${colors.schedule.stock}`,
              overflow: 'hidden',
            }}
          >
            <TableHeader
              currentSortKey={activeSortKey}
              sortDirection={activeSortDirection}
              onSort={handleActiveSort}
              isSortable={isActiveSortable}
            />
            {activeProducts.length === 0 ? (
              <Box sx={{ py: 3, textAlign: 'center', color: colors.text.secondary, fontSize: '0.875rem' }}>
                {t('stock.noStockData')}
              </Box>
            ) : (
              activeProducts.map((item) => renderStockRow(item, false))
            )}
          </Box>

          {/* Inactive Products - Collapsible Section */}
          {inactiveProducts.length > 0 && (
            <Accordion
              expanded={inactiveExpanded}
              onChange={(_, expanded) => setInactiveExpanded(expanded)}
              sx={{
                mt: 2,
                bgcolor: colors.background.white,
                borderRadius: '12px !important',
                border: `1px solid ${colors.border.light}`,
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: '48px',
                  '&.Mui-expanded': { minHeight: '48px' },
                  '& .MuiAccordionSummary-content': { my: 1, '&.Mui-expanded': { my: 1 } },
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <InactiveIcon sx={{ color: colors.text.muted }} fontSize="small" />
                  <Box sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}>
                    {t('stock.inactiveProducts')} ({inactiveProducts.length})
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '3fr 1.2fr 0.7fr 0.7fr 0.7fr 0.8fr 0.7fr 0.7fr',
                    py: 1,
                    px: 2,
                    bgcolor: colors.background.light,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: colors.text.secondary,
                    borderTop: `1px solid ${colors.border.light}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SortableHeaderCell
                      sortKey="product_id"
                      currentSortKey={inactiveSortKey}
                      sortDirection={inactiveSortDirection}
                      onSort={handleInactiveSort}
                      isSortable={isInactiveSortable('product_id')}
                    >
                      {t('dashboard.schedule.table.id')}
                    </SortableHeaderCell>
                    <Box>/</Box>
                    <SortableHeaderCell
                      sortKey="name"
                      currentSortKey={inactiveSortKey}
                      sortDirection={inactiveSortDirection}
                      onSort={handleInactiveSort}
                      isSortable={isInactiveSortable('name')}
                    >
                      {t('dashboard.schedule.table.product')}
                    </SortableHeaderCell>
                  </Box>
                  <SortableHeaderCell
                    sortKey="category.name"
                    currentSortKey={inactiveSortKey}
                    sortDirection={inactiveSortDirection}
                    onSort={handleInactiveSort}
                    isSortable={isInactiveSortable('category.name')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.schedule.table.category')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="stock"
                    currentSortKey={inactiveSortKey}
                    sortDirection={inactiveSortDirection}
                    onSort={handleInactiveSort}
                    isSortable={isInactiveSortable('stock')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('stock.currentStock')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="prediction_horizon_minutes"
                    currentSortKey={inactiveSortKey}
                    sortDirection={inactiveSortDirection}
                    onSort={handleInactiveSort}
                    isSortable={isInactiveSortable('prediction_horizon_minutes')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.schedule.table.horizon')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="expected_demand"
                    currentSortKey={inactiveSortKey}
                    sortDirection={inactiveSortDirection}
                    onSort={handleInactiveSort}
                    isSortable={isInactiveSortable('expected_demand')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.recommendations.demand')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="stock_depletes_at"
                    currentSortKey={inactiveSortKey}
                    sortDirection={inactiveSortDirection}
                    onSort={handleInactiveSort}
                    isSortable={isInactiveSortable('stock_depletes_at')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.schedule.urgent.depletesAt')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="last_transaction"
                    currentSortKey={inactiveSortKey}
                    sortDirection={inactiveSortDirection}
                    onSort={handleInactiveSort}
                    isSortable={isInactiveSortable('last_transaction')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('stock.lastTransaction')}
                  </SortableHeaderCell>
                  <Box sx={{ textAlign: 'center' }}>{t('stock.status')}</Box>
                </Box>
                {inactiveProducts.map((item) => renderStockRow(item, true))}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Ignored Products - Collapsible Section */}
          {ignoredProducts.length > 0 && (
            <Accordion
              expanded={ignoredExpanded}
              onChange={(_, expanded) => setIgnoredExpanded(expanded)}
              sx={{
                mt: 2,
                bgcolor: colors.background.white,
                borderRadius: '12px !important',
                border: `1px solid ${colors.border.light}`,
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: '48px',
                  '&.Mui-expanded': { minHeight: '48px' },
                  '& .MuiAccordionSummary-content': { my: 1, '&.Mui-expanded': { my: 1 } },
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <BlockIcon sx={{ color: colors.text.muted }} fontSize="small" />
                  <Box sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}>
                    {t('stock.ignoredProducts')} ({ignoredProducts.length})
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '3fr 1.2fr 0.7fr 0.7fr 0.7fr 0.8fr 0.7fr 0.7fr',
                    py: 1,
                    px: 2,
                    bgcolor: colors.background.light,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: colors.text.secondary,
                    borderTop: `1px solid ${colors.border.light}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SortableHeaderCell
                      sortKey="product_id"
                      currentSortKey={ignoredSortKey}
                      sortDirection={ignoredSortDirection}
                      onSort={handleIgnoredSort}
                      isSortable={isIgnoredSortable('product_id')}
                    >
                      {t('dashboard.schedule.table.id')}
                    </SortableHeaderCell>
                    <Box>/</Box>
                    <SortableHeaderCell
                      sortKey="name"
                      currentSortKey={ignoredSortKey}
                      sortDirection={ignoredSortDirection}
                      onSort={handleIgnoredSort}
                      isSortable={isIgnoredSortable('name')}
                    >
                      {t('dashboard.schedule.table.product')}
                    </SortableHeaderCell>
                  </Box>
                  <SortableHeaderCell
                    sortKey="category.name"
                    currentSortKey={ignoredSortKey}
                    sortDirection={ignoredSortDirection}
                    onSort={handleIgnoredSort}
                    isSortable={isIgnoredSortable('category.name')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.schedule.table.category')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="stock"
                    currentSortKey={ignoredSortKey}
                    sortDirection={ignoredSortDirection}
                    onSort={handleIgnoredSort}
                    isSortable={isIgnoredSortable('stock')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('stock.currentStock')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="prediction_horizon_minutes"
                    currentSortKey={ignoredSortKey}
                    sortDirection={ignoredSortDirection}
                    onSort={handleIgnoredSort}
                    isSortable={isIgnoredSortable('prediction_horizon_minutes')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.schedule.table.horizon')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="expected_demand"
                    currentSortKey={ignoredSortKey}
                    sortDirection={ignoredSortDirection}
                    onSort={handleIgnoredSort}
                    isSortable={isIgnoredSortable('expected_demand')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.recommendations.demand')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="stock_depletes_at"
                    currentSortKey={ignoredSortKey}
                    sortDirection={ignoredSortDirection}
                    onSort={handleIgnoredSort}
                    isSortable={isIgnoredSortable('stock_depletes_at')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('dashboard.schedule.urgent.depletesAt')}
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    sortKey="last_transaction"
                    currentSortKey={ignoredSortKey}
                    sortDirection={ignoredSortDirection}
                    onSort={handleIgnoredSort}
                    isSortable={isIgnoredSortable('last_transaction')}
                    sx={{ textAlign: 'center' }}
                  >
                    {t('stock.lastTransaction')}
                  </SortableHeaderCell>
                  <Box sx={{ textAlign: 'center' }}>{t('stock.status')}</Box>
                </Box>
                {ignoredProducts.map((item) => renderStockRow(item, true))}
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
}

export default StockAdjustment;